import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

/**
 * ThemeDetails Component
 *
 * Displays detailed information about each theme in an accordion format:
 * - Color palette preview
 * - Typography settings
 * - Other theme properties
 */
const ThemeDetails = ({ themes }) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Theme Details
      </Typography>
      {themes?.map(theme => (
        <Accordion key={theme.theme_id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              {theme.theme_name} ({theme.theme_code})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Color Palette:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: theme.primary_color,
                        borderRadius: 0.5,
                        mr: 1,
                        border: '1px solid #ccc',
                      }}
                    />
                    <Typography variant="body2">Primary</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: theme.secondary_color,
                        borderRadius: 0.5,
                        mr: 1,
                        border: '1px solid #ccc',
                      }}
                    />
                    <Typography variant="body2">Secondary</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: theme.background_color,
                        borderRadius: 0.5,
                        mr: 1,
                        border: '1px solid #ccc',
                      }}
                    />
                    <Typography variant="body2">Background</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: theme.surface_color,
                        borderRadius: 0.5,
                        mr: 1,
                        border: '1px solid #ccc',
                      }}
                    />
                    <Typography variant="body2">Surface</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Typography:
                </Typography>
                <Typography variant="body2">
                  Font: {theme.font_family}, Size: {theme.font_size_base}px
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Border Radius: {theme.border_radius}px, Spacing:{' '}
                  {theme.spacing_unit}px
                </Typography>
              </Grid>
              {theme.custom_css && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Custom CSS:
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#f5f5f5',
                      p: 2,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    }}
                  >
                    <pre>{theme.custom_css}</pre>
                  </Box>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ThemeDetails;
